// FAQ 富文本（Tiptap）序列化为 markdown 后，用户显式输入的空行以连续换行（\n{3,}）
// 的形式存储（每个空段落贡献额外的 \n\n）。但 markdown 语义会把连续空行折叠成一个
// 段落分隔，导致空行在渲染端（聊天气泡的 ReactMarkdown、编辑器回显的 Tiptap markdown
// 解析）全部丢失。这里提供双向转换：
// - 渲染/回显前：把连续空行显式化为 U+00A0（不换行空格）占位段落，避免被折叠
// - 保存前：把占位段落还原为纯空行，保持存储格式与旧数据一致，不把占位符写进数据
//
// 占位符选用原始 U+00A0 字符而非 &nbsp; 实体：markdown 的"空行"只认 space/tab，
// 含 U+00A0 的行会被解析成一个有内容的段落（视觉上是空行）；而 &nbsp; 实体
// ReactMarkdown 会解码、Tiptap 的 markdown 解析却当字面文本显示，两端不兼容。

/** 不换行空格（U+00A0），用作空行占位段落的内容 */
const NBSP = '\u00A0';

/** 匹配代码围栏（``` / ~~~），转换时跳过代码块内部，避免往代码内容里插入占位符 */
const CODE_FENCE_RE = /(```[\s\S]*?(?:```|$)|~~~[\s\S]*?(?:~~~|$))/g;

/** 单个空行还原/显式化时，最多保留的连续空行数，防止异常数据撑出超长空白 */
const MAX_BLANK_LINES = 5;

/**
 * 渲染/回显前调用：把 3 个及以上连续换行（显式空行）转换为 U+00A0 占位段落。
 * 例：`A\n\n\n\nB`（1 个空段落）→ `A\n\n \n\nB`
 */
export const preserveMarkdownBlankLines = (content: string): string => {
  if (!content) return content;
  return content
    .split(CODE_FENCE_RE)
    .map((segment, index) => {
      // split 捕获组：奇数下标是代码围栏本体，原样保留
      if (index % 2 === 1) return segment;
      return segment.replace(/\n{3,}/g, (match) => {
        // Tiptap markdown 序列化规则：段落间隔 \n\n，每个空段落额外贡献 \n\n
        const emptyCount = Math.max(1, Math.floor((match.length - 2) / 2));
        return '\n\n' + `${NBSP}\n\n`.repeat(Math.min(emptyCount, MAX_BLANK_LINES));
      });
    })
    .join('');
};

/**
 * 保存前调用：把仅含 U+00A0（兼容历史上可能写入的 &nbsp; 实体及其转义形态）的
 * 占位段落还原为空行，保持存储格式为纯连续换行。
 * 例：`A\n\n \n\nB` → `A\n\n\n\nB`
 */
export const normalizeMarkdownBlankLines = (content: string): string => {
  if (!content) return content;
  return content.replace(/(^|\n)[\t ]*(?:\\?&nbsp;|\u00A0)+[\t ]*(?=\n|$)/g, '$1');
};
